import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSetRecoilState } from 'recoil';
import { v4 as uuid } from 'uuid';
import {
    fetchEditorsJoin,
    fetchEditorsLeave,
} from '~/backend/connector-next-api/connector-next-components';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { useCurrentUserName } from '~/hooks/domain/useCurrentUserName';
import { otherEditorsPresenceState } from '~/state/atoms/presenceAtoms';
import { enqueueSnackbar } from '~/utils/notifications';
import { pendingEntryCount, usePendingChanges } from './usePendingChanges';

/**
 * Re-join cadence; keeps `lastSeenAt` fresh in the advisory registry.
 * MUST stay comfortably under the server's sweep TTL
 * (InMemoryEditorRegistry.Ttl, 45 s) — a heartbeat slower than the TTL
 * makes this client invisible to other editors between beats, defeating
 * the open-time concurrent-editing warning. 15 s gives two missed beats
 * of slack before the registry sweeps the entry.
 */
export const PRESENCE_HEARTBEAT_MS = 15_000;

/**
 * Advisory editor presence (write/sync design §4 — not a lock): registers
 * this client as an editor of the loaded connector patient, heartbeats,
 * and leaves on unload. On the first join, other active editors trigger a
 * non-blocking warning so concurrent editing is discovered at open time
 * rather than at save time via a 409.
 *
 * Each beat carries this client's dirty state (pending saveable entries)
 * and publishes the OTHER editors' presence — how many, and how many of
 * them report unsaved changes — to {@link otherEditorsPresenceState} for
 * the header indicator. A dirty-state flip triggers an immediate
 * off-schedule beat so "being edited" surfaces to others without waiting
 * out the interval.
 *
 * Presence is best-effort by design: a failed join/heartbeat/leave is
 * logged and otherwise ignored — the save workflow's optimistic
 * concurrency is the real conflict protection.
 */
export function useEditorPresence(): void {
    const session = useSessionOrNull();
    const queryClient = useQueryClient();
    const currentUserName = useCurrentUserName();
    const { state: pendingState } = usePendingChanges();
    const warnedRef = useRef(false);
    const setOtherEditors = useSetRecoilState(otherEditorsPresenceState);

    const patientId = session?.persistenceRegistry.patientRef?.patientId;
    // The registry requires a non-empty editor name (it is shown to other
    // users in the warning), so an unidentified user is named as such.
    const editorName = currentUserName ?? 'unknown';

    // Read at beat time via a ref: dirtiness changing must not tear down
    // and re-create the heartbeat (a leave+join would flicker this
    // client's presence for everyone else).
    const hasUnsavedChanges = pendingEntryCount(pendingState) > 0;
    const dirtyRef = useRef(hasUnsavedChanges);
    dirtyRef.current = hasUnsavedChanges;
    /** Set by the active heartbeat effect; null while no patient. */
    const beatNowRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        if (!patientId) {
            return;
        }
        const clientSessionId = uuid();
        let disposed = false;
        /**
         * The most recent join, settled or not. The leave waits for it.
         *
         * Join and leave are separate requests on separate connections, so a
         * DELETE fired immediately after a POST can reach the server first.
         * It then removes nothing, the POST lands behind it, and the entry it
         * creates belongs to a client that has already gone — a ghost nobody
         * heartbeats and nobody leaves, which every later session reads as
         * another user editing the patient until the registry's TTL sweeps
         * it. React's development double-invoke fires exactly that pair back
         * to back on every open, which is where this surfaces first, but any
         * short-lived mount can produce it.
         */
        let lastJoin: Promise<unknown> = Promise.resolve();

        const join = async (announce: boolean): Promise<void> => {
            try {
                const response = await fetchEditorsJoin({
                    pathParams: { patientId },
                    body: {
                        clientSessionId,
                        editorName,
                        hasUnsavedChanges: dirtyRef.current,
                    },
                });
                if (disposed) {
                    return;
                }
                const others = (response.editors ?? []).filter(
                    (editor) => editor.clientSessionId !== clientSessionId
                );
                // Every beat refreshes the live presence — the save
                // indicator shows the concurrent-editing risk for as long
                // as it exists, not only at open time.
                setOtherEditors({
                    count: others.length,
                    editing: others.filter(
                        (editor) => editor.hasUnsavedChanges === true
                    ).length,
                });
                if (!announce || warnedRef.current) {
                    return;
                }
                if (others.length > 0) {
                    warnedRef.current = true;
                    // A message node, not useIntl(): the snackbar renders
                    // under the app's IntlProvider, so this hook stays
                    // mountable outside one (the session bridge is).
                    enqueueSnackbar(
                        <FormattedMessage
                            id="saveChanges.otherEditors"
                            values={{ count: others.length }}
                        />,
                        { variant: 'warning' }
                    );
                }
            } catch {
                // Best-effort; the console stays quiet on transient network
                // noise and the 409 conflict path still protects saves.
                // The last known presence stays — the TTL bounds staleness.
            }
        };

        const beat = (announce: boolean): void => {
            lastJoin = join(announce);
        };

        beat(true);
        const timer = setInterval(() => beat(false), PRESENCE_HEARTBEAT_MS);
        beatNowRef.current = () => beat(false);

        return () => {
            disposed = true;
            clearInterval(timer);
            beatNowRef.current = null;
            setOtherEditors({ count: 0, editing: 0 });
            // After the join it undoes, never racing it — see `lastJoin`.
            void lastJoin
                .catch(() => undefined)
                .then(() =>
                    fetchEditorsLeave({
                        pathParams: { patientId, clientSessionId },
                    })
                )
                .then(() => {
                    // The worklist carries this presence on every row, and it
                    // refetches the moment the user lands back on it — which
                    // is before this leave reaches the server. Without this
                    // the row keeps its "Open" badge, freshly fetched rather
                    // than stale, until something else refetches the list.
                    void queryClient.invalidateQueries({
                        queryKey: ['api', 'patients'],
                    });
                })
                .catch(() => {
                    // Best-effort: the registry expires stale entries itself.
                });
        };
    }, [patientId, editorName, setOtherEditors, queryClient]);

    // A dirty-state flip beats immediately (an upsert, so harmless next to
    // the scheduled interval) — other viewers see "being edited" within a
    // round-trip instead of up to one heartbeat later.
    useEffect(() => {
        beatNowRef.current?.();
    }, [hasUnsavedChanges]);

    // A new patient may warn again.
    useEffect(() => {
        warnedRef.current = false;
    }, [patientId]);
}
